import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  type Expression,
  type ExpressionId,
  type Form0,
  type FormId,
  type FormNode,
  type FormStepId,
  type FormStyleElement,
  type Identity0,
  type IdentityFormData,
  type IdentityId,
  type InputId,
  type InputStepId,
  type InputSubstep2,
  type SectionStepId,
  type SignatureInformation,
  type Value0,
  type ValueId,
} from '~types'

export interface FormNodesState {
  formNodes: Record<FormStepId | SectionStepId | InputStepId, FormNode>
  savedFormNodes: Record<FormStepId | SectionStepId | InputStepId, FormNode>
  expressions: Record<ExpressionId, Expression>
  expressionValues: Record<ValueId, Value0>
  formIdMap: Record<string, string>
  formCaseOwner: Identity0 | null
  formCaseRequester: IdentityId | null
  caseId: string | null
  identityFormData: Record<string, IdentityFormData>
  completedSteps: Record<FormStepId, boolean> // Track which steps are completed
  formInReview: Form0 | null
  pageBreaks: Record<FormStepId, FormStyleElement[]>
  inputsFailingValidation: InputSubstep2[]
  preSubmitComments: Record<InputId, string>
  accessibleUrls: Record<string, string[]>
  signatureFormData: Record<InputStepId, SignatureInformation>
  formDataHashCache: Record<FormId, string>
}

const initialState: FormNodesState = {
  formNodes: {},
  savedFormNodes: {},
  expressions: {},
  expressionValues: {},
  formIdMap: {},
  formCaseOwner: null,
  formCaseRequester: null,
  caseId: null,
  identityFormData: {},
  completedSteps: {},
  formInReview: null,
  pageBreaks: {},
  inputsFailingValidation: [],
  preSubmitComments: {},
  accessibleUrls: {},
  signatureFormData: {},
  formDataHashCache: {},
}

const formNodesSlice = createSlice({
  name: 'formNodes',
  initialState,
  reducers: {
    setFormCaseOwner: (state, action: PayloadAction<{ formCaseOwner: Identity0 | null }>) => {
      state.formCaseOwner = action.payload.formCaseOwner
    },
    setFormCaseRequesterId: (state, action: PayloadAction<{ formCaseRequester: IdentityId }>) => {
      state.formCaseRequester = action.payload.formCaseRequester
    },
    setCaseId: (state, action: PayloadAction<{ caseId: string | null }>) => {
      state.caseId = action.payload.caseId
    },
    setFormNode: (
      state,
      action: PayloadAction<{
        nodeId: FormStepId | SectionStepId | InputStepId
        node: FormNode
      }>,
    ) => {
      state.formNodes[action.payload.nodeId] = action.payload.node
      if (action.payload.node.input?.form) {
        delete state.formDataHashCache[action.payload.node.input.form as FormId]
      }
    },
    setSavedFormNode: (
      state,
      action: PayloadAction<{ nodeId: FormStepId | SectionStepId | InputStepId; node: FormNode }>,
    ) => {
      state.savedFormNodes[action.payload.nodeId] = action.payload.node
    },
    removeFormNode: (
      state,
      action: PayloadAction<{ nodeId: FormStepId | SectionStepId | InputStepId }>,
    ) => {
      delete state.formNodes[action.payload.nodeId]
    },
    setExpressions: (state, action: PayloadAction<{ expressions: Record<string, Expression> }>) => {
      state.expressions = action.payload.expressions
    },
    setExpressionValues: (
      state,
      action: PayloadAction<{ expressionValues: Record<string, Value0> }>,
    ) => {
      state.expressionValues = action.payload.expressionValues
    },
    setFormIdMap: (state, action: PayloadAction<{ formIdMap: Record<string, string> }>) => {
      state.formIdMap = action.payload.formIdMap
    },
    setIdentityData: (
      state,
      action: PayloadAction<{
        instanceId: string
        identityFormData: IdentityFormData
      }>,
    ) => {
      state.identityFormData[action.payload.instanceId] = action.payload.identityFormData
    },
    markIdentityAsVirtualContact: (state, action: PayloadAction<{ instanceId: string }>) => {
      state.identityFormData[action.payload.instanceId] = {
        ...state.identityFormData[action.payload.instanceId],
        isVirtualContact: true,
      }
    },
    setStepCompletion: (
      state,
      action: PayloadAction<{
        stepId: FormStepId
        completed: boolean
      }>,
    ) => {
      state.completedSteps[action.payload.stepId] = action.payload.completed
    },
    clearFormNodes: (state) => {
      state.formNodes = {}
      state.expressions = {}
      state.expressionValues = {}
      state.formIdMap = {}
      state.completedSteps = {}
      state.caseId = null
      state.formDataHashCache = {}
    },
    clearFormCaseOwner: (state) => {
      state.formCaseOwner = null
    },
    clearFormCaseRequester: (state) => {
      state.formCaseRequester = null
    },
    setFormInReview: (state, action: PayloadAction<{ formInReview: Form0 | null }>) => {
      state.formInReview = action.payload.formInReview
    },
    setPageBreaks: (
      state,
      action: PayloadAction<{ stepId: FormStepId; pageBreaks: FormStyleElement[] }>,
    ) => {
      state.pageBreaks[action.payload.stepId] = action.payload.pageBreaks
    },
    setInputsFailingValidation: (
      state,
      action: PayloadAction<{ inputsFailingValidation: InputSubstep2[] }>,
    ) => {
      state.inputsFailingValidation = action.payload.inputsFailingValidation
    },
    setPreSubmitComments: (
      state,
      action: PayloadAction<{ preSubmitComments: Record<InputId, string> }>,
    ) => {
      state.preSubmitComments = action.payload.preSubmitComments
    },
    setAccessibleUrls: (
      state,
      action: PayloadAction<{ accessibleUrls: Record<string, string[]> }>,
    ) => {
      state.accessibleUrls = action.payload.accessibleUrls
    },
    setSignatureFormData: (
      state,
      action: PayloadAction<{
        inputStepId: InputStepId
        signatureInformation: SignatureInformation
      }>,
    ) => {
      state.signatureFormData[action.payload.inputStepId] = action.payload.signatureInformation
    },
    setFormDataHashCache: (state, action: PayloadAction<{ formId: FormId; hash: string }>) => {
      state.formDataHashCache[action.payload.formId] = action.payload.hash
    },
    clearFormDataHashCache: (state) => {
      state.formDataHashCache = {}
    },
    setFormNodesBatch: (
      state,
      action: PayloadAction<
        Array<{ nodeId: FormStepId | SectionStepId | InputStepId; node: FormNode }>
      >,
    ) => {
      action.payload.forEach(
        ({
          nodeId,
          node,
        }: {
          nodeId: FormStepId | SectionStepId | InputStepId
          node: FormNode
        }) => {
          state.formNodes[nodeId] = node
          if (node.input?.form) {
            delete state.formDataHashCache[node.input.form as FormId]
          }
        },
      )
    },
  },
})

export const {
  setFormCaseOwner,
  clearFormCaseOwner,
  setFormCaseRequesterId,
  clearFormCaseRequester,
  setCaseId,
  setFormNode,
  removeFormNode,
  setExpressions,
  setExpressionValues,
  setFormIdMap,
  setIdentityData,
  markIdentityAsVirtualContact,
  setStepCompletion,
  clearFormNodes,
  setFormInReview,
  setPageBreaks,
  setInputsFailingValidation,
  setPreSubmitComments,
  setAccessibleUrls,
  setSavedFormNode,
  setSignatureFormData,
  setFormDataHashCache,
  clearFormDataHashCache,
  setFormNodesBatch,
} = formNodesSlice.actions

export default formNodesSlice.reducer
